import { useTranslation } from 'react-i18next';

import type { ValidationErrorsExportRequest } from '@/lib/hooks/useValidationErrorsExports';
import { VALIDATION_PENDING_FIELD } from '@/lib/csv-offer/state';
import { ValidationErrorsSourceExportUnavailableReason } from '@/lib/graphql/generated/graphql';
import { useValidationErrorsExportAvailability } from '@/lib/hooks/useValidationErrorsExportAvailability';
import { productsMapState } from '@/states/products.state';
import { atom, useAtomValue } from 'jotai';
import { Download, Loader2 } from 'lucide-react';

import { Button } from './ui/button';

/**
 * Un produit attend sa revalidation (`updatingFields`, même signal que
 * `offerValidationPending`) : les erreurs exportées seraient périmées.
 */
const validationPendingAtom = atom((get) => {
  for (const product of get(productsMapState).values()) {
    if (
      product.activeVersion.updatingFields?.includes(VALIDATION_PENDING_FIELD)
    ) {
      return true;
    }
  }
  return false;
});

/**
 * Message under the button when the SO export is used instead. None for
 * `no_row_links`: every import is unlinked until the webhook-driven import is on,
 * so the SO export is simply the export, as before.
 */
const UNAVAILABLE_HINT_KEYS: Record<
  ValidationErrorsSourceExportUnavailableReason,
  string | null
> = {
  [ValidationErrorsSourceExportUnavailableReason.NoCsvDatasource]:
    'components.alert_sidebar.export_errors_unavailable.no_csv_datasource',
  [ValidationErrorsSourceExportUnavailableReason.NoRowLinks]: null,
  [ValidationErrorsSourceExportUnavailableReason.ValidationPending]:
    'components.alert_sidebar.export_errors_unavailable.validation_pending',
};

interface ValidationErrorsExportActionProps {
  dealId: string;
  onExport: (request: ValidationErrorsExportRequest) => void;
  isExporting: boolean;
}

/**
 * « Exporter mes erreurs » du panneau d'anomalies : le fichier d'origine avec
 * les colonnes d'erreurs quand l'API le permet, l'export de la phase 1 en
 * option ; sinon l'export de la phase 1, avec la raison.
 */
export function ValidationErrorsExportAction({
  dealId,
  onExport,
  isExporting,
}: ValidationErrorsExportActionProps) {
  const { t } = useTranslation();
  const availability = useValidationErrorsExportAvailability(dealId);
  const validationPending = useAtomValue(validationPendingAtom);

  const sourceAvailable =
    availability.status === 'source' && !validationPending;
  const unavailableReason = validationPending
    ? ValidationErrorsSourceExportUnavailableReason.ValidationPending
    : availability.status === 'so'
      ? availability.reason
      : null;
  const unavailableHintKey = unavailableReason
    ? UNAVAILABLE_HINT_KEYS[unavailableReason]
    : null;
  const exportSo = () =>
    onExport({ exportType: 'so', unavailableReason: unavailableReason });

  return (
    <div className="space-y-1.5">
      <Button
        type="button"
        variant="outline"
        className="w-full border-gray-300 font-medium"
        data-testid="alert-sidebar-export-errors"
        disabled={isExporting || availability.status === 'loading'}
        onClick={() =>
          sourceAvailable ? onExport({ exportType: 'source' }) : exportSo()
        }
      >
        {isExporting ? (
          <Loader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <Download className="mr-2 size-4" />
        )}
        {t('components.alert_sidebar.export_errors')}
      </Button>
      {sourceAvailable ? (
        <p className="text-center text-xs text-gray-700">
          {t('components.alert_sidebar.export_errors_source_hint')}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-gray-900 disabled:opacity-50"
            data-testid="alert-sidebar-export-errors-so"
            disabled={isExporting}
            onClick={exportSo}
          >
            {t('components.alert_sidebar.export_errors_so')}
          </button>
        </p>
      ) : (
        unavailableHintKey && (
          <p
            className="text-center text-xs text-gray-700"
            data-testid="alert-sidebar-export-errors-hint"
          >
            {t(unavailableHintKey)}
          </p>
        )
      )}
    </div>
  );
}
